import { datadogLogs } from "@datadog/browser-logs";
import classNames from "classnames";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { accountDeleteNotifications } from "@repo/shopify/api/deleteAccountNotification";
import useCustomerInfo from "@repo/shopify/hooks/useCustomerInfo";
import useCustomerInfoCaa from "@repo/shopify/hooks/useCustomerInfoCaa";
import useOrdersAmount from "@repo/shopify/hooks/useOrdersAmount";
import { extractId } from "@repo/shopify/utils/common";
import { validateNumber } from "@repo/shopify/utils/regex";

import { fetchUpdateCustomer } from "@api/lambdaApi";

import Button from "@atoms/Button";
import Input from "@atoms/Input";
import Link from "@atoms/Link";
import Select from "@atoms/Select";

import ChangePasswordForm from "@organisms/ChangePasswordForm";

import { MEMBERSHIP_TABLE } from "@const/constant";
import { NOTIFICATIONS } from "@const/notifications";
import type { TGender } from "@const/sizeRecommender";
import { normalizeGender, toStoredGender } from "@const/sizeRecommender";
import { URLS } from "@const/urls";

import useCheckPurchaseLimit from "@hooks/useCheckPurchaseLimit";
import useFormErrorHandler from "@hooks/useFormErrorHandler";
import useLanguage from "@hooks/useLanguage";

import useCustomer from "@queries/useCustomer";
import useCustomerCaa from "@queries/useCustomerCaa";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IPersonalInfoFormData {
  email: string;
  firstName: string;
  lastName: string;
  accountAcceptsMarketing: boolean;
}

interface ISizeFormData {
  gender: string;
  height: string;
  weight: string;
}

const GENDER_OPTIONS: { name: string; value: TGender | "" }[] = [
  { name: "Select Gender", value: "" },
  { name: "Male", value: "M" },
  { name: "Female", value: "F" },
];

interface IMembershipInfo {
  amount: number;
  coupon: string;
  discount: string;
}

type TMembership = "MEMBER" | "SILVER" | "GOLD" | "PLATINUM" | "VIP";

const MEMBERSHIP_INFO_KR: Record<TMembership, IMembershipInfo> = {
  MEMBER: { amount: 300000, coupon: "", discount: "1%" },
  SILVER: { amount: 2000000, coupon: "5%", discount: "3%" },
  GOLD: { amount: 5000000, coupon: "7%", discount: "5%" },
  PLATINUM: { amount: 10000000, coupon: "10%", discount: "7%" },
  VIP: { amount: 10000000, coupon: "15%", discount: "10%" },
};

const MEMBERSHIP_INFO_INTL: Record<TMembership, IMembershipInfo> = {
  MEMBER: { amount: 300, coupon: "", discount: "1%" },
  SILVER: { amount: 2000, coupon: "5%", discount: "3%" },
  GOLD: { amount: 5000, coupon: "7%", discount: "5%" },
  PLATINUM: { amount: 10000, coupon: "10%", discount: "7%" },
  VIP: { amount: 10000, coupon: "15%", discount: "10%" },
};

const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function AccountInfo() {
  const navigate = useNavigate();

  const { lang, currency } = useLanguage();
  const isJp = lang === "jp";

  const { customer, caaCustomer, token, logout, updateCaa, caaLoginProvider } = useCustomerStore();
  const { openModal, openToast, showFooter } = useLayoutStore();

  const { fetchCustomer, updateCustomer } = useCustomer();
  const { fetchCaaCustomer, updateCaaCustomer, subscribeCaaEmailMarketing, unsubscribeCaaEmailMarketing } = useCustomerCaa();

  const { purchaseLimit, orderTotal, remainingLimit } = useCheckPurchaseLimit();

  const classicCustomerInfo = useCustomerInfo(customer, "thisisneverthat");
  const caaCustomerInfo = useCustomerInfoCaa(caaCustomer, "thisisneverthat");

  const {
    socialLoginType: hookSocialLoginType,
    isPartnership,
    membership,
    isEmployee,
    isSocialAccount: hookIsSocialAccount,
    isDeleteRequest,
    membershipCalcDate,
    membershipInfo,
  } = isJp ? caaCustomerInfo : classicCustomerInfo;

  // jp(Hiko)는 신버전 앱이 customer에 provider 태그를 안 붙여줘서 tags 기반 판별이 불가능함.
  // 로그인 시점에 프론트에서 캡처해둔 caaLoginProvider로 대체한다(SocialLoginCaa.tsx 참고).
  // jp는 네이티브 비밀번호 계정 자체가 없어(전부 Hiko 경유) 이 값이 있으면 곧 소셜/이메일 로그인 계정.
  const isSocialAccount = isJp ? Boolean(caaLoginProvider) : hookIsSocialAccount;
  const socialLoginType = isJp ? (caaLoginProvider ?? "") : hookSocialLoginType;

  const personalInfoDefaultValues = useMemo(
    () =>
      isJp
        ? {
            firstName: caaCustomer?.firstName || "",
            lastName: caaCustomer?.lastName || "",
            email: caaCustomer?.emailAddress?.emailAddress || "",
            accountAcceptsMarketing: caaCustomer?.emailAddress?.marketingState === "SUBSCRIBED",
          }
        : {
            firstName: customer.firstName || "",
            lastName: customer.lastName || "",
            email: customer.email || "",
            accountAcceptsMarketing: customer.acceptsMarketing || false,
          },
    [isJp, customer, caaCustomer],
  );

  const sizeDefaultValues = useMemo(
    () => ({
      gender: normalizeGender(customer.gender?.value) ?? "",
      height: customer.height?.value || "",
      weight: customer.weight?.value || "",
    }),
    [customer],
  );

  /** 회원 탈퇴 요청 시 중복 클릭 방지하는 state */
  const [isRequesting, setIsRequesting] = useState(false);
  const [isPersonalInfoLoading, setIsPersonalInfoLoading] = useState<boolean>(false);
  const [isSizeLoading, setIsSizeLoading] = useState<boolean>(false);
  const [checkedAcceptsMarketing, setCheckedAcceptsMarketing] = useState<boolean>(
    personalInfoDefaultValues.accountAcceptsMarketing || false,
  );

  useEffect(() => {
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const namingOrder = useMemo(() => (lang === "ko" ? "eastern" : "western"), []);

  const MEMBERSHIP_INFO = lang === "ko" ? MEMBERSHIP_INFO_KR : MEMBERSHIP_INFO_INTL;

  const {
    register: registerPersonalInfo,
    getValues: getPersonalInfoValues,
    handleSubmit: handlePersonalInfoSubmit,
    setFocus,
    formState: { errors: personalInfoErrors },
  } = useForm<IPersonalInfoFormData>({ defaultValues: personalInfoDefaultValues });

  const { displayError, onCommonError } = useFormErrorHandler({
    values: getPersonalInfoValues(),
    errors: personalInfoErrors,
  });

  const {
    register: registerSize,
    getValues: getSizeValues,
    handleSubmit: handleSizeSubmit,
    formState: { errors: sizeErrors },
  } = useForm<ISizeFormData>({ defaultValues: sizeDefaultValues });

  const { displayError: displaySizeError, onCommonError: onSizeCommonError } = useFormErrorHandler({
    values: getSizeValues(),
    errors: sizeErrors,
  });

  const onSubmitPersonalInfo: SubmitHandler<IPersonalInfoFormData> = async (formData) => {
    if (JSON.stringify(formData) === JSON.stringify(personalInfoDefaultValues)) {
      openToast(NOTIFICATIONS.WARNING_SAME_ACCOUNT_INFO);
      return;
    }

    setIsPersonalInfoLoading(true);

    try {
      if (isJp) {
        if (formData.firstName !== personalInfoDefaultValues.firstName || formData.lastName !== personalInfoDefaultValues.lastName) {
          const updated = await updateCaaCustomer({ input: { firstName: formData.firstName, lastName: formData.lastName } });
          if (updated.customerUpdate.userErrors.length) return;
        }

        if (formData.accountAcceptsMarketing !== personalInfoDefaultValues.accountAcceptsMarketing) {
          const marketingErrors = formData.accountAcceptsMarketing
            ? (await subscribeCaaEmailMarketing({})).customerEmailMarketingSubscribe.userErrors
            : (await unsubscribeCaaEmailMarketing({})).customerEmailMarketingUnsubscribe.userErrors;
          if (marketingErrors.length) return;
        }

        const data = await fetchCaaCustomer();
        if (data?.customer) updateCaa(data.customer);

        openToast(NOTIFICATIONS.SUCCESS_MODIFY_ACCOUNT_INFO);
        return;
      }

      const response = await updateCustomer({
        customer: {
          acceptsMarketing: formData.accountAcceptsMarketing,
          firstName: formData.firstName,
          lastName: formData.lastName,
        },
        customerAccessToken: token.accessToken,
      });

      if (response?.customerUpdate?.customer) {
        await fetchCustomer({ customerAccessToken: token.accessToken });
        openToast(NOTIFICATIONS.SUCCESS_MODIFY_ACCOUNT_INFO);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);

      if (isJp) {
        datadogLogs.logger.error("[CAA][UpdateCustomer] Failed to Fetch", { message });
        throw new Error(`[CAA][UpdateCustomer] Failed to Fetch ${message}`);
      }

      throw new Error(message);
    } finally {
      setIsPersonalInfoLoading(false);
    }
  };

  const onSubmitSize: SubmitHandler<ISizeFormData> = async (formData) => {
    if (JSON.stringify(formData) === JSON.stringify(sizeDefaultValues)) {
      openToast(NOTIFICATIONS.WARNING_SAME_ACCOUNT_INFO);
      return;
    }

    setIsSizeLoading(true);

    try {
      const isCustomerMetafieldsUpdated = await fetchUpdateCustomer({
        domain: `thisisneverthat-${lang}`,
        customerId: customer.id,
        gender: formData.gender ? toStoredGender(formData.gender as TGender) : "",
        height: formData.height,
        weight: formData.weight,
      });

      if (isCustomerMetafieldsUpdated) {
        await fetchCustomer({ customerAccessToken: token.accessToken });
        openToast(NOTIFICATIONS.SUCCESS_MODIFY_ACCOUNT_INFO);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      setIsSizeLoading(false);
    }
  };

  /** 회원탈퇴 버튼 클릭 */
  const handleDeleteAccount = () => {
    openModal({
      title: "Account Deactivation",
      content: (
        <p>
          <Trans i18nKey="account.deactivation.ask" />
        </p>
      ),
      confirm: {
        label: "Confirm",
        onClick: () => {
          void handleNotification().then(() => {
            openModal(null);
          });
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          openModal(null);
        },
      },
    });
  };

  const handleDeletionSuccess = () => {
    logout();
    setTimeout(() => {
      openToast(NOTIFICATIONS.SUCCESS_LOGOUT);
    }, 300);
    navigate(URLS.HOME);
  };

  /** 회원 탈퇴 요청 시 고객 정보 슬랙으로 알림 */
  const handleNotification = async () => {
    if (isRequesting) return;

    setIsRequesting(true);

    if (isDeleteRequest) {
      handleDeletionSuccess();
      return;
    }

    const payload = isJp
      ? {
          domain: `thisisneverthat-${lang}`,
          customerId: extractId(caaCustomer?.id || ""),
          email: caaCustomer?.emailAddress?.emailAddress || "",
          name: `${caaCustomer?.lastName || ""} ${caaCustomer?.firstName || ""}`.trim(),
          phone: caaCustomer?.phoneNumber?.phoneNumber || "",
        }
      : {
          domain: `thisisneverthat-${lang}`,
          customerId: extractId(customer.id),
          email: customer.email || "",
          name: `${customer.lastName} ${customer.firstName}`.trim(),
          phone: customer.phone || customer?.defaultAddress?.phone || "",
        };

    try {
      const response = await accountDeleteNotifications(payload);

      if (response) {
        handleDeletionSuccess();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      setIsRequesting(false);
    }
  };

  // 등급 업데이트 기준 날짜 계산: 매달 1일 00:00 기준 3년 이내의 주문이면서 마지막 7일 이내가 아닌 주문의 실 결제 금액
  const { totalOrderAmount, isOrdersLoading } = useOrdersAmount({
    accessToken: token.accessToken,
    ...membershipCalcDate,
    skip: isJp,
  });

  // 다음 달 멤버십 등급
  const nextMonthMembership: TMembership | undefined = useMemo(() => {
    if (isJp || isEmployee || membership === "VIP") return;

    const calcAmount = Number(totalOrderAmount.toFixed(2));
    const filterAmount = Object.entries(MEMBERSHIP_INFO)
      .map(([key, value]) => ({ name: key, value: value.amount }))
      .filter((item) => item.value > calcAmount)
      .sort((a, b) => a.value - b.value);

    return filterAmount.length < 2 ? "VIP" : (filterAmount[0].name as TMembership);
  }, [isJp, isEmployee, membership, totalOrderAmount]);

  // 다음 멤버십 등급
  const nextMembership: TMembership | undefined = useMemo(() => {
    if (isJp || isEmployee || membership === "VIP") return;

    const calcAmount = Number(totalOrderAmount.toFixed(2));
    const filterAmount = Object.entries(MEMBERSHIP_INFO)
      .map(([key, value]) => ({ name: key, value: value.amount }))
      .filter((item) => item.value > calcAmount)
      .sort((a, b) => a.value - b.value);

    return filterAmount.length < 2 ? "VIP" : (filterAmount[1].name as TMembership);
  }, [isJp, isEmployee, membership, totalOrderAmount]);

  const nextMonth = useMemo(() => {
    const nextMonthIndex =
      new Date().getMonth() === 11
        ? new Date(new Date().getFullYear() + 1, new Date().getMonth() + 1).getMonth()
        : new Date(new Date().getFullYear(), new Date().getMonth() + 1).getMonth();

    return lang === "ko" ? nextMonthIndex + 1 : MONTH[nextMonthIndex];
  }, [lang]);

  // 다음 멤버십까지 남은 금액
  const amountLeft = useMemo(() => {
    if (isJp) return 0;

    return membership !== "VIP" ? (membershipInfo?.lower || 0) - totalOrderAmount : 0;
  }, [isJp, membership, totalOrderAmount, membershipInfo]);

  const percent = useMemo(() => {
    if (isJp) return 0;
    if (isPartnership) return (orderTotal / purchaseLimit) * 100;
    return (totalOrderAmount / (membershipInfo?.lower || 1)) * 100;
  }, [isJp, isPartnership, orderTotal, purchaseLimit, totalOrderAmount, membershipInfo]);

  return (
    <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-20">
      {lang !== "jp" && (
        <section className="pc:grid-cols-3 pc:col-span-4 col-span-8 grid h-fit gap-y-2.5">
          {!isEmployee && !isPartnership ? (
            <>
              <h2 className="text-title col-span-4 mb-2.5">Membership</h2>
              <span className="pc:col-span-1 col-span-2 row-start-2">{t("account.membership.current")}</span>
              <span className="pc:col-span-5 col-span-4 row-start-2">{membership}</span>
              <span className="pc:col-span-1 col-span-2 row-start-3">
                <Trans i18nKey="account.membership.next" values={{ month: nextMonth }} />
              </span>
              <span className="pc:col-span-5 col-span-4 row-start-3">{nextMonthMembership}</span>
              <span className="pc:col-span-1 col-span-2 row-start-4">
                <Trans i18nKey="account.membership.benefit.title" values={{ membership }} />
              </span>
              <div className="pc:col-span-5 col-span-4 row-start-4 flex flex-col gap-1">
                <span>
                  <Trans
                    i18nKey="account.membership.benefit.discount"
                    values={{ discount: MEMBERSHIP_INFO[membership as TMembership]?.discount }}
                  />
                </span>
                <span>
                  {MEMBERSHIP_INFO[membership as TMembership].coupon ? (
                    <Trans i18nKey="account.membership.benefit.code" values={{ code: MEMBERSHIP_INFO[membership as TMembership].coupon }} />
                  ) : null}
                </span>
              </div>
              <div className={classNames("col-span-8 mb-2.5 flex flex-col gap-1", isOrdersLoading ? "opacity-50" : "opacity-100")}>
                <div className="flex justify-between">
                  {/* 최근 3년동안 주문 금액 */}
                  <span>
                    <Trans
                      i18nKey="account.membership.amount.current"
                      values={{ amount: `${currency}${totalOrderAmount.toLocaleString()}` }}
                    />
                  </span>
                  {/* 다음 등급까지 남은 주문 금액 */}
                  <span className="text-end">
                    <Trans
                      i18nKey="account.membership.amount.next"
                      values={{ membership: nextMembership, amount: `${currency}${amountLeft.toLocaleString()}` }}
                    />
                  </span>
                </div>
                <div>
                  <span className="bg-grey flex h-1 w-full rounded-full">
                    <span className="inline-block h-full w-full rounded-full bg-black" style={{ width: `${percent}%` }} />
                  </span>
                </div>
              </div>

              <Button
                as="square"
                onClick={() => {
                  openModal({
                    title: "Membership",
                    content: (
                      <section className="flex flex-col gap-5">
                        <section className="flex flex-col gap-5">
                          {MEMBERSHIP_TABLE.map((item) => (
                            <div className="flex flex-col gap-2.5" key={item.membership}>
                              <span>{item.membership}</span>
                              <div className="tnt-gap-x col-span-3 grid grid-cols-2">
                                <span className="whitespace-pre">{item.benefits}</span>
                                <span className="whitespace-pre">{item.purchase_amount}</span>
                              </div>
                            </div>
                          ))}
                        </section>
                        <section className="flex flex-col gap-2.5">
                          <span>Description</span>
                          <ul className="flex list-disc flex-col gap-1 pl-4">
                            <li>{t("account.membership.policy.1")}</li>
                            <li>{t("account.membership.policy.2")}</li>
                            <li>{t("account.membership.policy.3")}</li>
                            <li>{t("account.membership.policy.4")}</li>
                            <li>{t("account.membership.policy.5")}</li>
                            <li>{t("account.membership.policy.6")}</li>
                            <li>{t("account.membership.policy.7")}</li>
                            <li>{t("account.membership.policy.8")}</li>
                            <li>{t("account.membership.policy.9")}</li>
                            <li>{t("account.membership.policy.10")}</li>
                            <li>{t("account.membership.policy.11")}</li>
                          </ul>
                        </section>
                      </section>
                    ),
                    isHideCancelButton: true,
                  });
                }}
                variant="secondary"
              >
                {t("account.membership.more_info")}
              </Button>
            </>
          ) : (
            <>
              {isEmployee && !isPartnership ? (
                <>
                  <h2 className="text-title col-span-4 mb-2.5">Membership</h2>
                  <span className="pc:col-span-1 col-span-2 row-start-2">나의 멤버십 등급</span>
                  <span className="pc:col-span-5 col-span-4 row-start-2">{membership}</span>
                  <span className="pc:col-span-1 col-span-2 row-start-3">
                    <Trans i18nKey="account.membership.benefit.title" values={{ membership: nextMonthMembership }} />
                  </span>

                  <span className="pc:col-span-5 col-span-4 row-start-3 mb-2.5">{t("account.membership.employee.benefits")}</span>
                </>
              ) : null}

              {isPartnership ? (
                <>
                  <h2 className="text-title col-span-8 mb-2.5">Toss Employee</h2>
                  <span className="pc:col-span-1 col-span-2 row-start-2">등급</span>
                  <span className="pc:col-span-5 col-span-4 row-start-2">TOSS</span>
                  <span className="pc:col-span-1 col-span-2 row-start-3">혜택</span>
                  <span className="pc:col-span-5 col-span-4 row-start-3 mb-2.5">최대 30% 할인</span>
                  <div className="col-span-8 mb-2.5 flex flex-col gap-1">
                    <div className="flex justify-between">
                      <span>{`총 주문 금액: ${currency}${orderTotal.toLocaleString()}`}</span>
                      <span className="text-end">{`잔여 한도: ${currency}${remainingLimit.toLocaleString()}`}</span>
                    </div>
                    <div>
                      <span className="bg-grey flex h-1 w-full rounded-full">
                        <span className="inline-block h-full w-full rounded-full bg-black" style={{ width: `${percent}%` }} />
                      </span>
                    </div>
                  </div>
                  <Button
                    as="text"
                    className="col-span-8"
                    onClick={() => {
                      openModal({
                        title: "TOSS EMPLOYEE",
                        content: (
                          <section className="flex flex-col gap-5">
                            <section className="flex flex-col gap-5">
                              <div className="flex flex-col gap-2.5">
                                <span>BENEFIT</span>
                                <div className="tnt-gap-x col-span-3 grid grid-cols-2">
                                  <span className="whitespace-pre">최대 30% 할인 혜택</span>
                                </div>
                              </div>
                            </section>
                            <section className="flex flex-col gap-2.5">
                              <span>Description</span>
                              <ul className="flex list-disc flex-col gap-1 pl-4">
                                <li>{t("toss_policy.1")}</li>
                                <li>{t("toss_policy.2")}</li>
                                <li>{t("toss_policy.3")}</li>
                                <li>{t("toss_policy.4")}</li>
                                <li>{t("toss_policy.5")}</li>
                                <li>{t("toss_policy.6")}</li>
                              </ul>
                            </section>
                          </section>
                        ),
                        isHideCancelButton: true,
                      });
                    }}
                  >
                    혜택 자세히 보기
                  </Button>
                </>
              ) : null}
            </>
          )}
        </section>
      )}

      <div className="pc:col-span-4 col-span-8 flex flex-col gap-20">
        <div className="flex flex-col gap-5">
          <h2 className="text-title">Personal Info</h2>
          <form
            className="pc:grid flex flex-col gap-2.5 break-all"
            onSubmit={handlePersonalInfoSubmit(onSubmitPersonalInfo, onCommonError)}
          >
            <Input
              disabled
              label={t("email")}
              readOnly
              value={(isJp ? caaCustomer?.emailAddress?.emailAddress : customer.email) ?? ""}
              {...registerPersonalInfo("email")}
            />
            <div className={classNames(lang === "ko" ? "pc:row-start-3" : "pc:row-start-2")}>
              <Input
                {...registerPersonalInfo("firstName")}
                {...displayError("firstName")}
                id="firstName"
                label={t("first_name")}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (namingOrder === "eastern" && event.key === "Tab") {
                    event.preventDefault();
                    event.stopPropagation();
                    setFocus("accountAcceptsMarketing");
                  }
                }}
                placeholder={t("first_name")}
              />
            </div>
            <div className={classNames(lang === "ko" ? "pc:row-start-2" : "pc:row-start-3")}>
              <Input
                {...registerPersonalInfo("lastName")}
                {...displayError("lastName")}
                id="lastName"
                label={t("last_name")}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (namingOrder === "eastern" && event.key === "Tab") {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!event.nativeEvent.isComposing) setFocus("firstName");
                  }
                }}
                placeholder={t("last_name")}
              />
            </div>
            <Input
              {...registerPersonalInfo("accountAcceptsMarketing", {
                onChange: () => {
                  setCheckedAcceptsMarketing((prev) => !prev);
                },
              })}
              checked={checkedAcceptsMarketing}
              id="accountAcceptsMarketing"
              label={t("account.personal_information.accepts_marketing")}
              type="checkbox"
            />
            <Button as="square" className="pc:row-start-6" disabled={isPersonalInfoLoading} loading={isPersonalInfoLoading} type="submit">
              {t("account.personal_information.update_information")}
            </Button>
          </form>
        </div>
        {lang === "en" && (
          <div className="flex flex-col gap-5">
            <h2 className="text-title">My size</h2>
            <div className="flex flex-col gap-1">
              <p className="leading-[145%]">Optional. Add your details to get better size recommendations. </p>
              <p className="font-fuzzy-light text-dark-grey">
                By using the recommender, you agree to the processing of your personal data in accordance with our{" "}
                <Link as="text" className="underline" href="https://intl.thisisneverthat.com/privacy-policy">
                  Privacy and Cookies Policy
                </Link>
              </p>
            </div>
            <form className="flex flex-col gap-2.5" onSubmit={handleSizeSubmit(onSubmitSize, onSizeCommonError)}>
              <Select as="square" id="gender" options={GENDER_OPTIONS} {...registerSize("gender")} />
              {sizeErrors.gender ? <span className="text-red -mt-1">{sizeErrors.gender.message}</span> : null}
              <Input
                {...registerSize("height", {
                  validate: (value) => validateNumber(value) || "Please enter numbers only",
                })}
                {...displaySizeError("height")}
                id="height"
                label="Height (cm)"
                placeholder="e.g. 170"
              />
              <Input
                {...registerSize("weight", {
                  validate: (value) => validateNumber(value) || "Please enter numbers only",
                })}
                {...displaySizeError("weight")}
                id="weight"
                label="Weight (kg)"
                placeholder="e.g. 80"
              />
              <Button as="square" className="pc:row-start-6" disabled={isSizeLoading} loading={isSizeLoading} type="submit">
                Submit
              </Button>
            </form>
          </div>
        )}
        {isSocialAccount ? (
          <div className="flex flex-col gap-5">
            <h2 className="text-title">Social Login</h2>
            <div className="flex flex-col gap-2.5">
              <p className="wrap-break-word capitalize">{t("account.social_login.change_password")}</p>
              <p className="border-grey w-fit items-center justify-center border px-3 py-1.5 capitalize">{socialLoginType}</p>
            </div>
          </div>
        ) : null}
        {!isSocialAccount && !isJp ? (
          <div className="flex flex-col gap-5">
            <h2 className="text-title">Change Password</h2>
            <ChangePasswordForm />
          </div>
        ) : null}
        <div className="flex flex-col gap-5">
          <h2 className="text-title">Account Deactivation</h2>
          <p className="wrap-break-word">{t("account.deactivation.request")}</p>
          <Button as="square" disabled={isRequesting} onClick={handleDeleteAccount}>
            {t("deactivate")}
          </Button>
        </div>
      </div>
    </section>
  );
}
